/**
 * assignmentFeedApi — 「전체 테스트」 목록의 유일한 데이터 입구.
 *
 * 화면은 `listAllAssignments()` 하나만 부르고, 그 안은 서버 목록 한 번이다 —
 * `GET /api/v2/exam-papers/assignments`(2026-09-06 신설 · dev `537792e` ·
 * 회신 `docs/exam-builder-v2/BE-20260906-reply-assignment-global-list.md`).
 *
 * ── 조립을 걷어냈다 (2026-09-06) ──
 * 그전에는 전체를 시계열로 주는 경로가 없어 시험지를 돌며 모았다(1+N). 그래서 왕복이 시험지 수만큼
 * 늘고, 400건이 넘으면 표를 못 그리고 사유만 적었으며, 거르기·정렬·쪽 나누기를 화면이 했다.
 * 회신이 「임시 1+N 조립은 걷어내셔도 됩니다」로 닫아 주어 그 셋을 전부 서버로 넘겼다 —
 * 상한도 사라졌다(서버가 쪽으로 준다).
 *
 * ── 이 파일에 남는 일은 둘이다 ──
 * ① **화면의 칸 이름을 서버 파라미터 이름으로 옮기는 것.** 화면은 「학생 이름」·「담당 선생님」처럼
 *    사람 말로 칸을 두고, 서버는 `q`·`in_charge_teacher` 로 받는다. 그 대응을 여기 한 곳에 둔다 —
 *    화면이 직접 파라미터를 짓게 두면 칸을 하나 늘릴 때마다 두 자리를 고쳐야 한다.
 * ② **범위를 벗어난 쪽을 마지막 쪽으로 당기는 것.** 조립이 쪽을 셈하던 때부터 지켜 온 약속이고,
 *    서버는 없는 쪽을 그냥 빈 목록으로 답하므로 이 자리가 없으면 화면이 「없습니다」라고 거짓말한다.
 *
 * ⚠ 「학생 이름」은 서버의 `q` 로 간다. `q` 는 **학생명 OR 시험지 제목**을 함께 훑으므로(회신 §1),
 * 시험지 제목이 그 글자를 품은 배정도 함께 걸린다. 칸 이름이 「학생 이름」인 채로 그러면 거짓이라
 * 화면의 자리표시를 「학생·시험지 검색」으로 바꿨다(AssignmentFeedPanel).
 */
import { listAssignments } from '../ExamPapersV2/examPaperApi';

/** 화면이 비운 칸은 조건에서 뺀다 — 빈 문자열로 거르면 아무것도 안 남는다 */
const text = (v) => (typeof v === 'string' ? v.trim() : (v == null ? '' : String(v).trim()));

/**
 * 화면 칸 → 서버 파라미터.
 *
 * 값은 다듬기만 하고 **판정하지 않는다** — 번호 칸에 글자를 넣으면 서버가 400 으로 말한다.
 * 여기서 조용히 빼면 조건 없는 전체가 돌아와 「그 번호의 배정이 이렇게 많다」가 된다(listPapers 와 같은 근거).
 */
export function toQuery(filters = {}) {
  return {
    assignmentId: text(filters.assignmentId),
    studentId: text(filters.studentId),
    q: text(filters.studentName),
    schoolName: text(filters.schoolName),
    inChargeTeacher: text(filters.teacher),
  };
}

/** 화면이 읽을 모양으로 추린다 — 서버가 더 실어 보내도 화면이 아는 것만 넘긴다 */
const shape = (r) => ({
  items: r.items,
  page: r.page,
  perPage: r.perPage,
  totalItems: r.totalItems,
  totalPages: r.totalPages,
  // 이 응답에 적용된 범위('all'·'mine'). 화면이 사람에게 그대로 말한다 — 계정 종류를 보고
  // 분기하지 않는 것이 v2 의 규칙이고, 그 규칙은 v1 시절 프런트가 userType 으로 경로를 골랐다가
  // 교사 계정에서 담당 학생이 하나도 안 나왔던 사고에서 나왔다(FE-20260826-v2-student-api.md).
  scope: r.scope ?? null,
});

/**
 * 목록 — 화면이 부르는 유일한 함수.
 *
 * 정렬은 서버 기본(`assigned_at` 내림차순 = 최근 출제 순)을 그대로 쓴다. 화면 문구가 그 말이고,
 * 회신 §2 4-1 이 그 기본을 확인했다 — 같은 것을 두 자리에서 정하지 않는다.
 *
 * @returns {{items, page, perPage, totalItems, totalPages, scope}} 서버 응답 그대로의 모양.
 */
export async function listAllAssignments(
  { page = 1, perPage = 20, filters = {} } = {},
  getAccessToken,
) {
  const query = toQuery(filters);
  const r = await listAssignments({ page, perPage, ...query }, getAccessToken);

  /**
   * 범위를 벗어난 쪽이면 **마지막 쪽으로 당긴다.**
   * 3쪽을 보다가 조건을 걸어 결과가 1쪽으로 줄면, 서버는 3쪽을 「없음」으로 답한다. 그대로 그리면
   * 화면이 「출제된 테스트가 없습니다」라고 적는데 조건에 맞는 것은 있다 — 사실과 다른 말이다.
   * 조립이 쪽을 셈하던 때부터 지켜 온 약속이라 서버로 옮기면서도 그대로 둔다.
   * 정말 0건일 때는 다시 부르지 않는다(`totalItems > 0`) — 빈 목록에 왕복을 하나 더 쓰지 않는다.
   */
  if (r.items.length === 0 && r.totalItems > 0 && r.totalPages >= 1 && r.page > r.totalPages) {
    return shape(await listAssignments({ page: r.totalPages, perPage, ...query }, getAccessToken));
  }
  return shape(r);
}

export default listAllAssignments;
